import m from '@bitstillery/mithril'
import {Button, Icon, ThemeSwitcher} from '@bitstillery/common/components/portal'
import {$t} from '@bitstillery/common/app'
import {classes, get_route} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'

import {$m, $s} from '@/app'
import {BarLanguageSwitcher} from '@/components/bar_language_switcher'
import {PricelistDownload} from '@/components/pricelist_download/pricelist_download'
import {CmsFragment} from '@/cms/runtime/fragments'

export class PanelMenu extends MithrilComponent {
    view(): m.Children {
        if ($s.panels.view.menu.collapsed) {
            return null
        }

        const route = m.route.get()
        const is_authenticated = Boolean($s.identity.token)

        return [
            <div
                className={classes('c-panel-menu-backdrop', 'c-panel-menu-backdrop--visible')}
                key='panel-menu-backdrop'
                onclick={() => ($s.panels.view.menu.collapsed = true)}
            />,
            <div className='c-panel-menu c-panel-menu--open' key='panel-menu'>
                <div className='c-panel-menu-body'>
                    <div className='panel-title'>
                        <Icon name='menuOpen' size='d' type='unset' />
                        <span>{$t('bar_main.main_menu_title')}</span>
                    </div>
                    <div className='c-panel-menu-mobile-controls'>
                        <button
                            aria-label={$t('bar_main.shop_menu_close_tip')}
                            className='c-panel-menu-collapse-bar'
                            title={$t('bar_main.shop_menu_close_tip')}
                            type='button'
                            onclick={() => ($s.panels.view.menu.collapsed = true)}
                        >
                            <Icon name='chevronDown' size='s' type='unset' />
                        </button>
                    </div>
                    <div className='c-panel-menu-scroll'>
                        <div className='c-panel-menu-block c-panel-menu-chrome'>
                            <BarLanguageSwitcher className='c-panel-menu-language' menu_align='left' />
                            {$s.settings.beta && <ThemeSwitcher className='c-panel-menu-theme' />}
                        </div>
                        {/* The same editable nav the breadcrumb bar shows beside its crumbs, so the drawer
                            carries the site sections without a second place to maintain them. Clicks
                            anywhere in the fragment close the drawer, whatever the fragment renders. */}
                        <div
                            className='c-panel-menu-block c-panel-menu-fragment'
                            onclick={() => ($s.panels.view.menu.collapsed = true)}
                        >
                            <CmsFragment name='cms-breadcrumb-menu' />
                        </div>
                        {is_authenticated ? this.authenticated_items(route) : this.guest_items(route)}
                    </div>
                </div>
            </div>,
        ]
    }

    // Drawer body for signed-in users: pricelist, favourites, my-portal and logout.
    private authenticated_items(route: string): m.Children {
        return [
            <div className='c-panel-menu-block'>
                <PricelistDownload
                    className='c-panel-menu-pricelist'
                    icon='download'
                    model={$s.offers.export}
                    with_filters={false}
                    with_text={$t('filters.pricelist.download')}
                />
            </div>,
            <div className='c-panel-menu-block'>
                <Button
                    icon='favourite-o'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set(get_route('/shop/favourites'))
                    }}
                    text={$t('offer.my_favourites_tip')}
                    variant='menu'
                />
            </div>,
            <div className='c-panel-menu-block'>
                <Button
                    active={route.startsWith('/my-portal/orders')}
                    icon='myOrders'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/my-portal/orders')
                    }}
                    text={$t('my_portal.options.my_orders')}
                    variant='menu'
                />
                <Button
                    active={route.startsWith('/my-portal/settings')}
                    icon='cog'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/my-portal/settings')
                    }}
                    text={$t('my_portal.options.settings')}
                    variant='menu'
                />
                <Button
                    active={route.startsWith('/my-portal/email')}
                    icon='email'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/my-portal/email')
                    }}
                    text={$t('my_portal.options.email_preferences')}
                    variant='menu'
                />
            </div>,
            <div className='c-panel-menu-block'>
                <Button
                    active={route === '/terms-conditions'}
                    icon='legal'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/terms-conditions')
                    }}
                    text={$t('my_portal.terms.label')}
                    variant='menu'
                />
                {$s.promotion.active_grace ? (
                    <Button
                        active={route.startsWith('/my-portal/promotions')}
                        icon='party'
                        onclick={() => {
                            $s.panels.view.menu.collapsed = true
                            void m.route.set('/my-portal/promotions')
                        }}
                        text={$t('my_portal.promotions')}
                        variant='menu'
                    />
                ) : null}
            </div>,
            <div className='c-panel-menu-block'>
                <Button
                    icon='logout'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        $m.identity.logout()
                    }}
                    text={$t('my_portal.logout')}
                    type='info'
                    variant='menu'
                />
            </div>,
        ]
    }

    // Drawer body for signed-out users: the login/register CTAs and terms that would
    // otherwise sit in the header bar, collapsed here on mobile like the shop nav.
    private guest_items(route: string): m.Children {
        return [
            <div className='c-panel-menu-block'>
                <Button
                    icon='login'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/login')
                    }}
                    text={$t('menu.login')}
                    variant='menu'
                />
                <Button
                    icon='profilePlus'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/register')
                    }}
                    text={$t('registration.signup')}
                    variant='menu'
                />
            </div>,
            <div className='c-panel-menu-block'>
                <Button
                    active={route === '/terms-conditions'}
                    icon='legal'
                    onclick={() => {
                        $s.panels.view.menu.collapsed = true
                        void m.route.set('/terms-conditions')
                    }}
                    text={$t('my_portal.terms.label')}
                    variant='menu'
                />
            </div>,
        ]
    }
}
